"use client"

import { useEffect, useRef, useState } from "react"
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  Download,
  FileAudio,
  Image as ImageIcon,
  Loader2,
  Music2,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react"
import { motion } from "framer-motion"

import { LISTAS } from "@/data"
import Topnav from "@/components/topnav"
import useOrientation from "@/hooks/useOrientation"

import type {
  ImportedRow,
  Playlist,
  Song,
} from "./metadataTypes"

import {
  buttonClass,
  downloadText,
  emptySong,
  formatBytes,
  importedToSong,
  normalizePlaylist,
  serialize,
  createImportedRow,
} from "./metadataUtils"

import MetadataEditor from "./components/editor/MetadataEditor"

const inputClass =
  "w-full min-w-0 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white outline-none transition placeholder:text-white/20 focus:border-white/30 focus:bg-black/40"

function Field({
  label,
  value,
  onChange,
  placeholder,
  className = "",
}: {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}) {
  return (
    <label className={`block min-w-0 ${className}`}>
      {label && (
        <span className="mb-1.5 block text-[10px] uppercase tracking-[.18em] text-white/35">
          {label}
        </span>
      )}

      <input
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className={inputClass}
      />
    </label>
  )
}

function SectionTitle({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04]">
          {icon}
        </div>

        <div className="min-w-0">
          <h2 className="truncate text-sm font-medium">
            {title}
          </h2>

          {description && (
            <p className="truncate text-[10px] text-white/30">
              {description}
            </p>
          )}
        </div>
      </div>

      {action}
    </div>
  )
}

function InfoStat({
  label,
  value,
}: {
  label: string
  value: string
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-2">
      <p className="truncate text-[8px] uppercase tracking-[.15em] text-white/25">
        {label}
      </p>
      <p className="mt-1 truncate font-mono text-xs text-white/70">
        {value}
      </p>
    </div>
  )
}

export default function MetadataExtractor() {
  const { isPortrait, isLandscape, isStandalone } = useOrientation()

  const inputRef = useRef<HTMLInputElement>(null)

  const [playlist, setPlaylist] = useState<Playlist>(() =>
    normalizePlaylist(LISTAS[0])
  )

  const [collection, setCollection] = useState<Playlist[]>(() =>
    (LISTAS as unknown[]).map(normalizePlaylist)
  )

  const [playlistIndex, setPlaylistIndex] = useState(0)
  const [rows, setRows] = useState<ImportedRow[]>([])
  const [selectedRow, setSelectedRow] = useState<string | null>(null)
  const [playlistName, setPlaylistName] = useState("LISTAS")
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSelector, setShowSelector] = useState(false)

  const selected = rows.find(r => r.id === selectedRow)
  const selectedCount = rows.filter(r => r.selected).length

  const hasChanges =
  JSON.stringify(playlist) !==
  JSON.stringify(collection[playlistIndex])


  const changePlaylist = (patch: Partial<Playlist>) => {
    setPlaylist(p => ({ ...p, ...patch }))
    setSaved(false)
  }

  const changeSong = (
    index: number,
    patch: Partial<Song>
  ) => {
    setPlaylist(p => ({
      ...p,
      playlistData: p.playlistData.map((song, i) =>
        i === index ? { ...song, ...patch } : song
      ),
    }))
    setSaved(false)
  }

  const changeRow = (
    id: string,
    patch: Partial<ImportedRow>
  ) => {
    setRows(rows =>
      rows.map(row =>
        row.id === id ? { ...row, ...patch } : row
      )
    )
    setSaved(false)
  }

  const processFiles = async (files: FileList | File[]) => {
    const audio = Array.from(files).filter(file =>
      file.type.startsWith("audio/") ||
      /\.(mp3|mpeg|wav|m4a|flac|ogg)$/i.test(file.name)
    )

    if (!audio.length) {
      setError("No se encontraron archivos de audio.")
      return
    }

    setError(null)
    setLoading(true)
    setProgress(0)

    const imported: ImportedRow[] = []

    for (let i = 0; i < audio.length; i++) {
      imported.push(
        await createImportedRow(
          audio[i],
          playlist.link
        )
      )

      setProgress(
        Math.round(((i + 1) / audio.length) * 100)
      )
    }

    setRows(current => [...current, ...imported])

    if (!selectedRow && imported[0]) {
      setSelectedRow(imported[0].id)
    }

    setLoading(false)
  }

  const addImported = () => {
    const selectedRows = rows.filter(r => r.selected)

    if (!selectedRows.length) {
      setError("Selecciona al menos un track.")
      return
    }

    setPlaylist(current => {
      const start = current.playlistData.length
      const songs = selectedRows.map((row, i) =>
        importedToSong(row, start + i, current.link)
      )

      return {
        ...current,
        playlistData: [
          ...current.playlistData,
          ...songs,
        ].map((song, i) => ({
          ...song,
          index: i,
        })),
      }
    })

    setSaved(false)
  }

  const addManual = () => {
    setPlaylist(current => ({
      ...current,
      playlistData: [
        ...current.playlistData,
        emptySong(current.playlistData.length),
      ],
    }))

    setSaved(false)
  }

  const removeSong = (index: number) => {
    setPlaylist(current => ({
      ...current,
      playlistData: current.playlistData
        .filter((_, i) => i !== index)
        .map((song, i) => ({
          ...song,
          index: i,
        })),
    }))

    setSaved(false)
  }

  const moveSong = (
    index: number,
    direction: -1 | 1
  ) => {
    const target = index + direction

    setPlaylist(current => {
      if (
        target < 0 ||
        target >= current.playlistData.length
      ) {
        return current
      }

      const songs = [...current.playlistData]
      ;[songs[index], songs[target]] = [
        songs[target],
        songs[index],
      ]

      return {
        ...current,
        playlistData: songs.map((song, i) => ({
          ...song,
          index: i,
        })),
      }
    })

    setSaved(false)
  }

  const save = () => {
    setCollection(current =>
      current.map((item, i) =>
        i === playlistIndex
          ? structuredClone(playlist)
          : item
      )
    )

    setSaved(true)
  }

  const reset = () => {
    const source = collection[playlistIndex]
    if (!source) return

    setPlaylist(structuredClone(source))
    setSaved(false)
  }

  const selectPlaylist = (index: number) => {
    const item = collection[index]
    if (!item) return

    setPlaylistIndex(index)
    setPlaylist(structuredClone(item))
    setShowSelector(false)
    setSaved(true)
  }

  const exportSingle = () => {
    const name =
      playlist.id.replace(/[^a-zA-Z0-9_-]/g, "_") ||
      "playlist"

    downloadText(
      `export const ${name} = ${serialize(playlist)}\n`,
      `${name}.ts`
    )
  }

  const exportAll = () => {
    const name =
      playlistName.replace(/[^a-zA-Z0-9_$]/g, "_") ||
      "LISTAS"

    const data = collection.map((item, i) =>
      i === playlistIndex ? playlist : item
    )

    downloadText(
      `export const ${name} = ${serialize(data)}\n`,
      `${name}.ts`
    )
  }

  const removeRow = (id: string) => {
    const row = rows.find(r => r.id === id)

    if (row?.imageUrl) {
      URL.revokeObjectURL(row.imageUrl)
    }

    setRows(current => current.filter(r => r.id !== id))

    if (selectedRow === id) {
      setSelectedRow(null)
    }
  }

  useEffect(() => {
    return () =>
      rows.forEach(row => {
        if (row.imageUrl) URL.revokeObjectURL(row.imageUrl)
      })
  }, [])

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden overscroll-none touch-pan-y">
      <Topnav />


      <div
        className={`
          inset-0
          flex
          w-full
          flex-col
          //overflow-hidden
          select-none          
        `}
      >
        <div
       
          className={`
            flex-1
            min-h-0
            h-full
            overflow-x-hidden
            overflow-y-scroll
            scrollbar-hide
            overscroll-hidden
           ${isPortrait ? "pt-0" : ""}
           ${isStandalone ? "mb-40 h-[100dvh]" : ""}
           ${isLandscape ? "h-[calc(100%-3.7rem)] rounded-xl border border-white/15 bg-[var(--movil-nav)] overscroll-y-auto" : ""}

          `}
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "none",
            overscrollBehaviorX: "none",
          }}
        >

      <motion.div
        className={`motion-content relative flex w-full flex-col select-none ${
          isLandscape
            ? "h-[calc(100dvh-3.7rem)] overflow-hidden rounded-xl border border-white/15 bg-[var(--movil-nav)]"
            : "h-[100dvh]"
        }`}
      >
        
    {/* <div className="relative h-[100dvh] w-full overflow-hidden">
      <Topnav />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`h-full overflow-y-auto bg-white/5 p-2 lg:p-4 ${
          isLandscape
            ? "h-[calc(100%-3.7rem)] rounded-xl border border-white/10"
            : ""
        }`}
      > */}
        <div 
          className={`mx-auto flex h-full w-full flex-col overflow-x-hidden overflow-y-auto overscroll-none no-scrollbar ${
              isLandscape
                ? "max-w-[1500px] px-4 py-4"
                : ""
            } ${
              isPortrait
                ? "p-4 pt-12 pb-24"
                : ""
            } ${
              isStandalone
                ? "pt-15 pb-30"
                : ""
            }`}
            > {/* className="mx-auto max-w-[1500px] pb-32" */} {/* className="relative flex min-h-0 w-full flex-1 flex-col overflow-x-hidden overflow-y-auto bg-[#050509] text-white no-scrollbar" */}


            <header
              className={`mb-3 flex flex-wrap items-center justify-between gap-3 ${
                isPortrait ? "pt-0" : ""
              } ${isStandalone ? "pt-20" : ""}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Music2 size={17} className="text-white/50" />
            
                  <span className="text-[9px] uppercase tracking-[.3em] text-white/35">
                    Metadata Studio
                  </span>
            
                  {hasChanges && (
                    <span className="inline-flex items-center gap-1 rounded-md border border-amber-400/20 bg-amber-400/10 px-1.5 py-0.5 text-[8px] uppercase tracking-wider text-amber-300/80">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-300" />
                      Modificado
                    </span>
                  )}
                </div>
                
                <h1 className="mt-1 text-xl font-semibold">
                  MP3 Playlist Builder
                </h1>
                
                <p className="text-xs text-white/30">
                  Extrae, edita, ordena y exporta.
                </p>
              </div>
                
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={reset}
                  className={buttonClass}
                >
                  <RotateCcw size={14} />
                  Restaurar
                </button>
                
                <button
                  type="button"
                  onClick={exportSingle}
                  className={buttonClass}
                >
                  <Download size={14} />
                  Exportar
                </button>
                
                <button
                  type="button"
                  onClick={exportAll}
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm text-black"
                >
                  <Download size={14} />
                  .ts
                </button>
              </div>
            </header>

          {/* <header
            className={`mb-3 flex flex-wrap items-center justify-between gap-3 ${
              isPortrait ? "pt-14" : ""
            } ${isStandalone ? "pt-20" : ""}`}
          >
            <div>
              <div className="flex items-center gap-2">
                <Music2 size={17} className="text-white/50" />
                <span className="text-[9px] uppercase tracking-[.3em] text-white/35">
                  Metadata Studio
                </span>
              </div>

              <h1 className="mt-1 text-xl font-semibold">
                MP3 Playlist Builder
              </h1>

              <p className="text-xs text-white/30">
                Extrae, edita, ordena y exporta.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={reset}
                className={buttonClass}
              >
                <RotateCcw size={14} />
                Restaurar
              </button>

              <button
                onClick={exportSingle}
                className={buttonClass}
              >
                <Download size={14} />
                Exportar
              </button>

              <button
                onClick={exportAll}
                className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm text-black"
              >
                <Download size={14} />
                .ts
              </button>
            </div>
          </header> */}

          {/* SELECTOR */}

          <section className="relative mb-3 rounded-2xl border border-white/10 bg-white/[.035]">
            <button
              onClick={() => setShowSelector(v => !v)}
              className="flex w-full items-center justify-between p-3 text-left"
            >
              <div className="flex items-center gap-3">
                <img
                  src={playlist.imgUrl}
                  className="h-10 w-10 rounded-xl object-cover"
                  alt=""
                />

                <div>
                  <p className="text-[9px] uppercase text-white/30">
                    Playlist activa
                  </p>

                  <p className="text-sm">
                    {playlist.title || "Sin título"}
                  </p>
                </div>
              </div>

              <ChevronDown
                size={16}
                className={showSelector ? "rotate-180" : ""}
              />
            </button>

            {showSelector && (
              <div className="absolute inset-x-0 top-full z-50 mt-2 rounded-2xl border border-white/10 bg-[#111] p-1.5">
                {collection.map((item, i) => (
                  <button
                    key={item.id || i}
                    onClick={() => selectPlaylist(i)}
                    className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-white/5"
                  >
                    <img
                      src={item.imgUrl}
                      className="h-9 w-9 rounded-lg object-cover"
                      alt=""
                    />

                    <span className="flex-1 truncate text-xs">
                      {item.title || "Sin título"}
                    </span>

                    {i === playlistIndex && (
                      <Check size={14} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </section>

          <MetadataEditor
            playlist={playlist}
            rows={rows}
            selected={selected}
            selectedCount={selectedCount}
            loading={loading}
            progress={progress}
            dragging={dragging}
            error={error}
            inputRef={inputRef}
            setDragging={setDragging}
            processFiles={processFiles}
            changeRow={changeRow}
            changePlaylist={changePlaylist}
            changeSong={changeSong}
            addImported={addImported}
            addManual={addManual}
            removeRow={removeRow}
            removeSong={removeSong}
            moveSong={moveSong}
            save={save}
            saved={saved}
            selectRow={setSelectedRow}
            closeSelected={() => setSelectedRow(null)}
          />

          
        </div>
      </motion.div>
      </div>
      </div>
    </div>
  )
}